import { useRef, useState, isValidElement, type ReactNode, type ReactElement } from 'react'
import { Check, Copy } from 'lucide-react'
import { cn } from '@/lib/cn'

interface CopyablePreProps extends React.HTMLAttributes<HTMLPreElement> {
  children?: ReactNode
}

/**
 * Extract the language label from the className of a <code> child.
 * rehype-highlight adds "language-bash", "language-json", etc.
 * Returns a short display label (e.g. "bash") or null if not found.
 */
function extractLanguage(children: ReactNode): string | null {
  if (!isValidElement(children)) return null
  const el = children as ReactElement<{ className?: string }>
  const cls: string = el.props.className ?? ''
  const match = /\blanguage-(\w+)\b/.exec(cls)
  return match?.[1] ?? null
}

/** Map technical language identifiers to friendlier display labels. */
const LANG_DISPLAY: Record<string, string> = {
  bash: 'bash',
  sh: 'sh',
  shell: 'shell',
  json: 'json',
  javascript: 'js',
  js: 'js',
  typescript: 'ts',
  ts: 'ts',
  python: 'python',
  py: 'python',
  go: 'go',
  sql: 'sql',
  yaml: 'yaml',
  toml: 'toml',
  http: 'http',
  text: 'text',
  plaintext: 'text',
}

/**
 * A <pre> wrapper that adds:
 *   - a clipboard copy button in the top-right corner
 *   - a language label badge in the top-left corner (when a language class is present)
 *
 * Used via react-markdown's `components` prop to replace the default <pre> element.
 *
 * Security: copies only the rendered text content of the code block,
 * never writes to hidden fields or localStorage.
 */
export function CopyablePre({ children, className, ...props }: CopyablePreProps) {
  const preRef = useRef<HTMLPreElement>(null)
  const [copied, setCopied] = useState(false)

  const lang = extractLanguage(children)
  const langLabel = lang ? (LANG_DISPLAY[lang] ?? lang) : null

  async function handleCopy() {
    const text = preRef.current?.innerText ?? ''
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard write failed (no permission in some sandboxed environments)
      // — fail silently from the user perspective.
    }
  }

  return (
    <div className="group relative my-4">
      {langLabel !== null && (
        <span
          aria-hidden
          className="absolute left-3 top-2 select-none rounded-sm bg-muted/80 px-1.5 py-0.5 font-mono text-[10px] leading-tight text-muted-fg"
        >
          {langLabel}
        </span>
      )}
      <pre
        ref={preRef}
        className={cn(
          'overflow-x-auto rounded-md border border-border bg-muted/60 p-4 text-sm leading-relaxed',
          langLabel !== null && 'pt-7',
          className,
        )}
        {...props}
      >
        {children}
      </pre>
      <button
        type="button"
        onClick={() => { void handleCopy() }}
        aria-label={copied ? 'Copied to clipboard' : 'Copy code to clipboard'}
        className={cn(
          'absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded border border-border bg-bg text-muted-fg opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
          copied && 'opacity-100 text-green-600',
        )}
      >
        {copied ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
      </button>
    </div>
  )
}
